import { computed, type ComputedRef } from 'vue';
import { amplitudeLib } from 'freedom-shared';

export const SEARCH_FORM_ANIMATED_FLOW_FLAG =
  'avfree_search_form_animated_flow';

export const ANIMATED_FLOW_VARIANT = {
  CONTROL: 'control',
  ANIMATED: 'animated',
} as const;

export type AnimatedFlowVariant =
  (typeof ANIMATED_FLOW_VARIANT)[keyof typeof ANIMATED_FLOW_VARIANT];

interface AnimatedFlowExperiment {
  isExperimentLoading: ComputedRef<boolean>;
  isAnimated: ComputedRef<boolean>;
  isControl: ComputedRef<boolean>;
  variant: ComputedRef<AnimatedFlowVariant | null>;
}

/**
 * Reads the `avfree_search_form_animated_flow` variant.
 *
 * Reads `experiment` and `isExperimentLoading` directly (rather than going through the
 * shared `checkExperimentVariant`) so the computed re-evaluates once `start()` resolves
 * and variants become available — same pattern as `useQuickBookingExperiment`.
 */
export const useAnimatedFlowExperiment = (): AnimatedFlowExperiment => {
  const { experiment, isExperimentLoading } =
    amplitudeLib.useAmplitudeExperiment();

  const variant = computed<AnimatedFlowVariant | null>(() => {
    if (isExperimentLoading.value) {
      return null;
    }
    const key = experiment.value?.variant?.(
      SEARCH_FORM_ANIMATED_FLOW_FLAG,
    )?.key;
    if (key === ANIMATED_FLOW_VARIANT.ANIMATED) {
      return ANIMATED_FLOW_VARIANT.ANIMATED;
    }
    if (key === ANIMATED_FLOW_VARIANT.CONTROL) {
      return ANIMATED_FLOW_VARIANT.CONTROL;
    }
    return null;
  });

  return {
    isExperimentLoading: computed(() => isExperimentLoading.value),
    isAnimated: computed(
      () => variant.value === ANIMATED_FLOW_VARIANT.ANIMATED,
    ),
    isControl: computed(() => variant.value === ANIMATED_FLOW_VARIANT.CONTROL),
    variant,
  };
};

/**
 * Reads the current variant outside of a component context, e.g. when tagging
 * analytics events fired from non-Vue helpers.
 */
export const getAnimatedFlowVariant = (): AnimatedFlowVariant | undefined => {
  const { experiment } = amplitudeLib.useAmplitudeExperiment();
  const key = experiment.value?.variant?.(SEARCH_FORM_ANIMATED_FLOW_FLAG)?.key;
  if (key === ANIMATED_FLOW_VARIANT.ANIMATED) {
    return ANIMATED_FLOW_VARIANT.ANIMATED;
  }
  if (key === ANIMATED_FLOW_VARIANT.CONTROL) {
    return ANIMATED_FLOW_VARIANT.CONTROL;
  }
  return undefined;
};
